// Contrat des événements et de la vie locale (M21, lot L5-C1, H7-05, D-225, D-253, D-254) : calendrier
// des événements de la collectivité, organisateur (service ou tiers), dates, lieu, réservation d'équipement
// (M19, chevauchement refusé par la base, R11), autorisation d'occupation du domaine public (M09),
// budget prévu et engagements de dépense rattachés (M06, montants et états lus dans leur module),
// publication par le secrétaire général, puis fréquentation et bilan. La publication ne vaut pas
// autorisation d'occuper un espace. Le calendrier public a son schéma strict, sans budget ni objet interne.
import { z } from 'zod';

const uuid = z.string().uuid('Identifiant invalide');
const jour = z.iso.date('Date AAAA-MM-JJ attendue');
const instant = z.iso.datetime({ offset: true, message: 'Date et heure ISO 8601 attendue' });
const heure = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Heure HH:MM attendue');
const texte = (max: number, min = 1) => z.string().trim().min(min).max(max);
const facultatif = (max: number) => z.string().trim().max(max).optional();

export const TYPE_NUMERO_EVENEMENT = 'EVT';

export const CATEGORIES_EVENEMENT = {
  culture: 'Culture',
  sport: 'Sport',
  ceremonie: 'Cérémonie officielle',
  fete: 'Fête et animation',
  marche_foire: 'Marché ou foire',
  reunion_publique: 'Réunion publique',
  autre: 'Autre',
} as const;
export type CategorieEvenement = keyof typeof CATEGORIES_EVENEMENT;
const CODES_CATEGORIES = Object.keys(CATEGORIES_EVENEMENT) as [CategorieEvenement, ...CategorieEvenement[]];

export const ETATS_EVENEMENT = { en_preparation: 'En préparation', publie: 'Publié', tenu: 'Tenu, bilan saisi', annule: 'Annulé' } as const;
export type EtatEvenement = keyof typeof ETATS_EVENEMENT;

export const ORGANISATEURS_EVENEMENT = { service: 'Service de la collectivité', tiers: 'Organisateur extérieur' } as const;
export type TypeOrganisateur = keyof typeof ORGANISATEURS_EVENEMENT;

/** Longueur minimale du texte publié sur le portail (D-253). */
export const LONGUEUR_MIN_TEXTE_PUBLIC_EVENEMENT = 10;

// --- Requêtes ---------------------------------------------------------------------------------------

const champsEvenement = {
  intitule: texte(200, 3),
  description: facultatif(4000),
  categorie: z.enum(CODES_CATEGORIES),
  organisateur_type: z.enum(['service', 'tiers']),
  organisateur_service_id: uuid.optional(),
  organisateur_nom: facultatif(200),
  organisateur_contact: facultatif(200),
  debut: instant,
  fin: instant,
  lieu: texte(300, 2),
  budget_prevu: z.number().int().min(0).max(1_000_000_000_000).optional(),
  texte_public: facultatif(2000),
};

export const schemaCreationEvenement = z
  .strictObject(champsEvenement)
  .refine((v) => Date.parse(v.fin) > Date.parse(v.debut), { message: 'La fin de l\'événement suit son début', path: ['fin'] })
  .refine((v) => v.organisateur_type !== 'service' || Boolean(v.organisateur_service_id), { message: 'Choisir le service organisateur', path: ['organisateur_service_id'] })
  .refine((v) => v.organisateur_type !== 'tiers' || Boolean(v.organisateur_nom && v.organisateur_nom.length >= 2), { message: 'Nommer l\'organisateur extérieur', path: ['organisateur_nom'] });
export type CreationEvenement = z.infer<typeof schemaCreationEvenement>;

/** Modification tant que l'événement est en préparation (mêmes contrôles que la création). */
export const schemaModificationEvenement = schemaCreationEvenement;

/** Réservation liée : une réservation existante de la collectivité, ou une demande au guichet par M19. */
export const schemaReservationEvenement = z
  .strictObject({
    reservation_id: uuid.optional(),
    equipement_id: uuid.optional(),
    date: jour.optional(),
    heure_debut: heure.optional(),
    heure_fin: heure.optional(),
    participants: z.number().int().positive().max(100_000).optional(),
  })
  .refine((v) => Boolean(v.reservation_id) !== Boolean(v.equipement_id), { message: 'Lier une réservation ou demander un équipement, pas les deux', path: ['reservation_id'] })
  .refine((v) => !v.equipement_id || (v.date && v.heure_debut && v.heure_fin), { message: 'Date et heures exigées pour une demande', path: ['date'] })
  .refine((v) => !v.heure_debut || !v.heure_fin || v.heure_debut < v.heure_fin, { message: 'L\'heure de fin suit l\'heure de début', path: ['heure_fin'] });
export type ReservationEvenement = z.infer<typeof schemaReservationEvenement>;

export const schemaAutorisationEvenement = z.strictObject({ autorisation_id: uuid });
export const schemaDepenseEvenement = z.strictObject({ engagement_id: uuid });
export const schemaAnnulationEvenement = z.strictObject({ motif: texte(500, 3) });
export const schemaBilanEvenement = z.strictObject({ frequentation: z.number().int().min(0).max(10_000_000), bilan: texte(8000, 10) });
export type BilanEvenement = z.infer<typeof schemaBilanEvenement>;

export const schemaFiltreEvenements = z.object({
  du: jour.optional(),
  au: jour.optional(),
  etat: z.enum(['en_preparation', 'publie', 'tenu', 'annule']).optional(),
  limite: z.coerce.number().int().min(1).max(500).default(300),
});

// --- Réponses ---------------------------------------------------------------------------------------

export interface ResumeEvenement {
  id: string;
  numero: string;
  intitule: string;
  categorie: CategorieEvenement;
  debut: string;
  fin: string;
  lieu: string;
  organisateur: string;
  etat: EtatEvenement;
  budget_prevu: number | null;
  fictif: boolean;
}

/** Objet lié, lu dans son module d'origine ; visible faux sans le droit de voir ce domaine. */
export interface LienReservation {
  id: string;
  visible: boolean;
  numero: string | null;
  equipement: string | null;
  date: string | null;
  heure_debut: string | null;
  heure_fin: string | null;
  etat: string | null;
  etat_libelle: string | null;
  paiement: string | null;
}

export interface LienAutorisation {
  id: string;
  visible: boolean;
  numero: string | null;
  categorie: string | null;
  debut: string | null;
  fin: string | null;
  etat: string | null;
  couvre_evenement: boolean | null;
}

export interface LienDepense {
  engagement_id: string;
  visible: boolean;
  numero: string | null;
  objet: string | null;
  tiers: string | null;
  montant: number | null;
  etat: string | null;
}

export interface FicheEvenement extends ResumeEvenement {
  description: string | null;
  organisateur_type: TypeOrganisateur;
  organisateur_service: { id: string; nom: string } | null;
  organisateur_nom: string | null;
  organisateur_contact: string | null;
  texte_public: string | null;
  publie_le: string | null;
  publie_par: string | null;
  reservation: LienReservation | null;
  autorisation: LienAutorisation | null;
  depenses: LienDepense[];
  /** Somme des engagements visibles non annulés ni refusés, en francs CFA. */
  depenses_engagees: number | null;
  frequentation: number | null;
  bilan: string | null;
  bilan_le: string | null;
  motif_annulation: string | null;
  transitions: { code: string; libelle: string; vers: string }[];
  alertes: string[];
}

/** Événement du calendrier public : informations publiées seulement (ni budget, ni réservation, ni organisateur). */
export const schemaEvenementPublic = z
  .object({
    id: z.string().uuid(),
    intitule: z.string(),
    categorie: z.enum(CODES_CATEGORIES),
    debut: z.string(),
    fin: z.string(),
    lieu: z.string(),
    texte_public: z.string(),
    statut: z.enum(['a_venir', 'en_cours', 'passe', 'annule']),
    fictif: z.boolean(),
  })
  .strict();
export type EvenementPublic = z.infer<typeof schemaEvenementPublic>;
